import React from "react";
import { FieldGroupRow, RequestAnalysisStatus } from "../../../models/intake";
import "./QuickEntryFieldConfidence.scss";

// Spec #34100 / F1b-rois, reconciled with the F1 UX mockup + TDD (AB#35066 /
// bug 35064).
//
// ORIGINAL scope (#34100, PR 9381): this row-adornment rendered THREE things -
// a per-row confidence dot, a "Source / No source" grounding button, and the
// transient extraction-status affordance. The confidence dot + "Source" button
// were built here BEFORE the mockup's inline grounding glyph existed.
//
// RECONCILIATION (authoritative docs win over the closed #34100 spec, per the
// project's TDD/PRD-over-ADO-spec rule): the F1 UX mockup ("Quick Entry Master
// Demo v6", named authoritative by tdd-feature-31605 §Status) and the feature
// TDD split these signals across DIFFERENT, non-overlapping affordances:
//   - CONFIDENCE / provenance  -> the LEFT row-status icon (green check = AI
//     confident, blue "user" = manual entry, amber triangle = low-confidence,
//     red = required-not-extracted, neutral = optional). tdd-feature-31605
//     line 65 ("green check (AI confident) / orange triangle (low confidence) /
//     red bang (required, not extracted) … user-entered values switch to a
//     'user' icon"). Implemented by getQuickEntryRowIcon in
//     QuickEntryFieldGroupList.tsx.
//   - GROUNDING (reveal source) -> the INLINE ≡ glyph next to each doc-sourced
//     value. tdd-feature-31605 line 66 ("Each value has an inline grounding
//     glyph -> docApi.scrollToField(...) … No-source values show a distinct
//     icon -> informational toast"). Implemented by QuickEntryGroundingIcon.tsx.
//   - PROVENANCE tag -> the "From Doc / From database" chips (line 71).
// The mockup shows NEITHER a right-side confidence dot NOR a "Source" text
// button (only the Edit button sits on the right). So the dot + button were a
// redundant second rendering of signals the left icon and the ≡ glyph already
// own, and are removed here to match the mockup and the project no-redundancy
// rule. See master-spec-bug-batch-31605.md §Grounding reconciliation.
//
// WHAT THIS COMPONENT STILL OWNS: the transient extraction-STATUS affordance
// (AC13) - a loading spinner while extraction is InProgress and distinct
// Failed / RetryExceeded markers. These are real runtime states the completed-
// state mockup does not depict, and neither the left icon nor the ≡ glyph
// conveys them, so they are NOT redundant and stay.

// Maps a Quick Entry FieldGroupRow id (F1a's stable row ids) to the camelCase
// RequestAnalysisData field name the backend keys confidence/grounding by
// (ResolvingRequestAnalysisRetriever projects FieldIdentifier -> camelCase).
// A row with no single corresponding contract field maps to null. Still exported
// as the ONE source of truth for "which contract field does this row ground to" -
// the per-value grounding glyph (AB#35066, QuickEntryGroundingIcon in
// DataEntryDetails.renderQuickEntryRowContent) resolves a scalar row's grounding
// field key from this same map.
//
// AB#35422: the two Service Date rows are keyed here too. The contract grounds
// service dates under indexed names ("ServiceDates[i].From"/".To") that no fixed
// row id could match, so ResolvingRequestAnalysisRetriever translates them to the
// two stable keys below - and only for the range the panel actually displays
// (the first with BOTH ends set, which is how seedExtractedServiceDate and
// serviceDateDocSourced pick it). Before that they mapped to null and the glyph
// always fell through to the no-source toast.
const ROW_ID_TO_CONFIDENCE_KEY: Readonly<Record<string, string>> = {
    requestTypePurpose: "requestType",
    authorizationDate: "authorizationDate",
    requestDate: "requestDate",
    caseNumber: "caseNumber",
    clinicResponsible: "clinicName",
    sensitiveInfo: "sensitiveInfoDeclarations",
    recordsRequested: "infoReleaseSections",
    serviceDateFrom: "serviceDateFrom",
    serviceDateTo: "serviceDateTo",
};

// One source of truth for the row -> contract grounding field key. A row with no
// single contract field returns null and the glyph degrades gracefully to the
// no-source toast.
export function confidenceKeyForRow(rowId: string): string | null {
    return ROW_ID_TO_CONFIDENCE_KEY[rowId] ?? null;
}

// AB#38128. Two of the keys above are mapped so the row can compute doc-sourcing, but the PRODUCER
// never grounds them: ConfigurationGroundingGate.DefaultFieldScope is 14 fields (the three scalar
// dates + ServiceDates, RequestType, RequestPurpose, CaseNumber, ClinicName and the six Requestor*
// fields), and AB#35066 AC3 records the exclusion as a permanent design boundary - these are
// closed-enum multi-selects with no single source span to point at.
//
// So for these rows findFieldGrounding can only ever return null, and the glyph could only ever
// render its no-grounding variant: title "Source location not available yet", click -> "No source
// location is available for this field." On a value the extraction DID produce that reads as a denial
// that the document was the source, and "yet" promises a location that will never arrive. Suppressing
// the glyph is the honest rendering; the row keeps its provenance signal through the status badge,
// which reads (isAiPreSelected || isDocSourced).
//
// Deliberately keyed on the CONTRACT FIELD, not the row id.
//
// This list mirrors a value that lives in Azure App Configuration on the producer
// (intake:grounding:fieldScope), NOT in code - and AB#35285 is a precedent for widening it. So this
// is only the FAST PATH for the common case: the caller must also let a live FieldGrounding win when
// it disagrees, or the day either field is added to that config the SPA would silently hide a glyph
// that now works. Both keys are already wired end to end on the ROIS side, so that payload can
// arrive with no ROIS change at all. See the call site in DataEntryDetails.renderRowContent.
const NEVER_GROUNDED_CONFIDENCE_KEYS: ReadonlySet<string> = new Set([
    "infoReleaseSections",
    "sensitiveInfoDeclarations",
]);

/**
 * Whether a row's grounding glyph can ever resolve to a document location.
 *
 * False for a row with no contract field at all, and for the closed-enum multi-selects the producer
 * does not ground. Callers must gate the glyph on this as well as on doc-sourcing.
 */
export function rowCanBeGrounded(rowId: string): boolean {
    const key = confidenceKeyForRow(rowId);
    // Decision 1: no contract field at all, so there is nothing for a grounding to match against.
    // This class is wider than the two denied keys and is worth naming, because two of its members
    // ARE doc-sourceable and so did render a (permanently no-source) glyph before AB#38128:
    // receivedDate and dueDate. requestId is in the class too but hardcodes isAiPreSelected=false,
    // so it can never be doc-sourced and never reached the glyph either way.
    if (key === null) {
        return false;
    }
    // Decision 2: mapped, but outside the producer's default located-field scope.
    return !NEVER_GROUNDED_CONFIDENCE_KEYS.has(key);
}

export interface QuickEntryFieldConfidenceProps {
    readonly row: FieldGroupRow;
    readonly status?: RequestAnalysisStatus;
}

/**
 * Transient extraction-STATUS adornment for one Quick Entry row (AC13).
 *
 * While an extraction is InProgress the row shows a pending spinner; Failed /
 * RetryExceeded each show a distinct failure marker; Completed and every other
 * state (no analysis / Unavailable) render nothing - confidence is conveyed by
 * the left row-status icon and grounding by the inline ≡ glyph (see the file
 * header for the mockup/TDD reconciliation), so this adornment is silent once
 * extraction succeeds, matching the mockup (only the Edit button on the right).
 */
const QuickEntryFieldConfidence: React.FC<QuickEntryFieldConfidenceProps> = (props) => {
    const { row, status } = props;

    if (status === RequestAnalysisStatus.InProgress) {
        return (
            <span
                data-testid={`qe-status-${row.id}`}
                className="qe-field-status qe-field-status--loading"
                title="Extracting…"
                aria-label="Extraction in progress"
            />
        );
    }
    if (status === RequestAnalysisStatus.Failed) {
        return (
            <span
                data-testid={`qe-status-${row.id}`}
                className="qe-field-status qe-field-status--failed"
                title="Extraction failed"
                aria-label="Extraction failed"
            />
        );
    }
    if (status === RequestAnalysisStatus.RetryExceeded) {
        return (
            <span
                data-testid={`qe-status-${row.id}`}
                className="qe-field-status qe-field-status--retry-exceeded"
                title="Extraction retries exhausted"
                aria-label="Extraction retries exhausted"
            />
        );
    }

    // Completed / Available / Unavailable / no analysis: no adornment. Confidence
    // is the left row-status icon; grounding is the inline ≡ glyph.
    return null;
};

export default QuickEntryFieldConfidence;
